import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

/**
 * The background-verification decision dialog, shared by the onboarding
 * pipeline and the guide detail page so there is one implementation of the
 * notes/reference contract rather than two that drift.
 */

export type VerificationAction = 'send' | 'pass' | 'fail' | 'retry';

interface ActionCopy {
  title: string;
  body: string;
  confirmLabel: string;
  confirmColor: 'primary' | 'success' | 'error';
  /** Whether the provider reference field is offered for this action. */
  showReference: boolean;
}

const ACTION_COPY: Record<VerificationAction, ActionCopy> = {
  send: {
    title: 'Send to SpringVerify',
    body: "Record that you have handed this applicant's name, email and phone number to SpringVerify. They stay in Background Check until you record the result.",
    confirmLabel: 'Mark as sent',
    confirmColor: 'primary',
    showReference: true,
  },
  pass: {
    title: 'Verified by SpringVerify',
    body: 'Record that SpringVerify cleared this applicant. This unlocks profile setup for them — it does not approve them or make them visible.',
    confirmLabel: 'Mark as verified',
    confirmColor: 'success',
    showReference: true,
  },
  fail: {
    title: 'Record a failure',
    body: 'Record that SpringVerify did not clear this applicant.',
    confirmLabel: 'Mark as failed',
    confirmColor: 'error',
    showReference: true,
  },
  retry: {
    title: 'Re-open verification',
    body: 'Send this applicant back to the start of the background check so their details can be submitted again.',
    confirmLabel: 'Re-open',
    confirmColor: 'primary',
    showReference: false,
  },
};

interface VerificationActionDialogProps {
  action: VerificationAction | null;
  guideName?: string;
  /** Reference captured earlier, pre-filled so a decision keeps the same id. */
  existingReference?: string;
  isPending?: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (input: { notes: string; reference?: string }) => void;
}

export function VerificationActionDialog({
  action,
  guideName,
  existingReference,
  isPending = false,
  error,
  onClose,
  onConfirm,
}: VerificationActionDialogProps) {
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');

  useEffect(() => {
    if (action) {
      setNotes('');
      setReference(existingReference ?? '');
    }
  }, [action, existingReference]);

  if (!action) return null;

  const copy = ACTION_COPY[action];
  const trimmedNotes = notes.trim();

  return (
    <Dialog open onClose={isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DialogContentText sx={{ mb: 2 }}>
          {guideName ? `${guideName} — ${copy.body}` : copy.body}
        </DialogContentText>

        {action === 'fail' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            These notes are internal. The applicant only sees a generic message
            asking them to contact support.
          </Alert>
        )}

        <TextField
          label="Internal notes"
          required
          fullWidth
          multiline
          minRows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={isPending}
          sx={{ mb: 2 }}
        />

        {copy.showReference && (
          <TextField
            label="SpringVerify reference ID"
            fullWidth
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            disabled={isPending}
            helperText="The candidate id from the SpringVerify dashboard. Optional, but it is what links this applicant to their check."
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={copy.confirmColor}
          disabled={!trimmedNotes || isPending}
          onClick={() =>
            onConfirm({
              notes: trimmedNotes,
              reference: reference.trim() || undefined,
            })
          }
        >
          {isPending ? 'Saving…' : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
